/* @jsx h */
import {
  defineAsyncComponent,
  defineComponent,
  h,
  ref,
  computed,
  nextTick,
  onMounted,
  watch,
} from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { RightSheet, RelativeTimeLabel } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import {
  STUB_AGENTS,
  STUB_MESSAGES,
  STUB_PROJECTS,
  getAgent,
} from "../stub-data";
import {
  MessageBlock,
  AGENT_COLOR_MAP,
  PROJECT_COLOR_MAP,
} from "../renderers/index";
import { ToolCallList } from "../renderers/ToolCallList";
import { AgentAvatar } from "../components/AgentSidebar";
import type {
  ComposerAgent,
  ComposerMention,
} from "../components/AgentMessageComposer";
import { useIntelligenceStore } from "../store";
import { ProjectBindingsPanel } from "./ProjectSettings";
import type {
  ThreadMessage,
  MerchantAgent,
  AgentOutput,
  ModelDefinition,
} from "../types";

const AgentMessageComposer = defineAsyncComponent(() =>
  import("../components/AgentMessageComposer").then(
    (module) => module.AgentMessageComposer,
  ),
);

/** Maps a thread_messages row from apps/intelligence into the frontend's ThreadMessage shape. */
function toThreadMessage(raw: {
  id: string;
  senderType: "user" | "agent";
  senderId: string;
  outputs: AgentOutput[];
  usage?: ThreadMessage["usage"];
  createdAt: string;
}): ThreadMessage {
  if (raw.senderType === "user") {
    return {
      id: raw.id,
      senderType: "user",
      senderId: raw.senderId,
      senderName: "You",
      senderInitials: "ME",
      outputs: raw.outputs,
      createdAt: raw.createdAt,
    };
  }
  return {
    id: raw.id,
    senderType: "agent",
    senderId: raw.senderId,
    senderName: "Alex",
    senderAvatarIcon: "insights",
    senderAvatarColor: "violet",
    outputs: raw.outputs,
    usage: raw.usage,
    createdAt: raw.createdAt,
  };
}

const formatTokens = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;

const formatCost = (usd: number) =>
  `$${usd < 0.01 ? usd.toFixed(4) : usd.toFixed(2)}`;

/** List-price USD cost of one message, or null when its model has no pricing. */
const messageCost = (
  usage: NonNullable<ThreadMessage["usage"]>,
  models: ModelDefinition[],
  fallbackModelId: string | null,
): number | null => {
  const modelId = usage.modelId ?? fallbackModelId;
  const pricing = models.find((m) => m.id === modelId)?.pricing;
  if (!pricing) return null;
  return (
    (usage.inputTokens * pricing.inputPerMTok +
      usage.outputTokens * pricing.outputPerMTok) /
    1_000_000
  );
};

const UserBubble = defineComponent<{ initials: string }>({
  name: "UserBubble",
  props: ["initials"],
  setup(props) {
    return () => (
      <div class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border border-base-content/10 bg-base-content/10 text-[10px] font-semibold text-base-content/55">
        {props.initials}
      </div>
    );
  },
});

const MessageRow = defineComponent<{
  message: ThreadMessage;
  cost?: number | null;
}>({
  name: "MessageRow",
  props: ["message", "cost"],
  setup(props) {
    return () => {
      const msg = props.message;

      if (msg.senderType === "user") {
        return (
          <div class="group grid grid-cols-[28px_minmax(0,1fr)] gap-3 py-3">
            <UserBubble
              initials={
                msg.senderInitials ?? msg.senderName.slice(0, 2).toUpperCase()
              }
            />
            <div class="min-w-0">
              <div class="mb-1 flex items-center gap-2">
                <span class="text-sm font-medium text-base-content">
                  {msg.senderName === "You" ? "You" : msg.senderName}
                </span>
                <RelativeTimeLabel
                  datetime={msg.createdAt}
                  class="text-xs text-base-content/30"
                />
              </div>
              <div class="flex flex-col gap-2">
                {msg.outputs.map((output, index) => (
                  <MessageBlock key={index} output={output} />
                ))}
              </div>
            </div>
          </div>
        );
      }

      const agentForAvatar: MerchantAgent | null = msg.senderAvatarColor
        ? {
            id: msg.senderId,
            templateId: "",
            templateType: "",
            name: msg.senderName,
            avatarIcon: msg.senderAvatarIcon ?? "smart_toy",
            avatarColor: msg.senderAvatarColor,
            description: "",
          }
        : (getAgent(msg.senderId) ?? null);

      const colors = agentForAvatar
        ? AGENT_COLOR_MAP[agentForAvatar.avatarColor]
        : AGENT_COLOR_MAP.violet;

      return (
        <div class="group grid grid-cols-[28px_minmax(0,1fr)] gap-3 border-t border-base-content/10 py-4 first:border-t-0">
          {agentForAvatar ? (
            <AgentAvatar agent={agentForAvatar} size="md" />
          ) : (
            <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/10">
              <span
                class="material-symbols-outlined text-primary"
                style="font-size:16px"
              >
                auto_awesome
              </span>
            </div>
          )}
          <div class="min-w-0">
            <div class="mb-1.5 flex items-center gap-2">
              <span class={`text-sm font-medium ${colors.text}`}>
                {msg.senderName}
              </span>
              <span class="text-xs text-base-content/30">
                {new Date(msg.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              {msg.usage && (
                <span class="text-xs text-base-content/25">
                  {formatTokens(msg.usage.inputTokens)} in /{" "}
                  {formatTokens(msg.usage.outputTokens)} out
                  {props.cost != null && ` · ${formatCost(props.cost)}`}
                </span>
              )}
            </div>
            <div class="flex flex-col gap-2">
              {msg.toolCalls && msg.toolCalls.length > 0 && (
                <ToolCallList toolCalls={msg.toolCalls} />
              )}
              {msg.outputs.map((output, index) => (
                <MessageBlock
                  key={index}
                  output={output}
                  streaming={index === msg.outputs.length - 1 && msg.streaming}
                />
              ))}
            </div>
          </div>
        </div>
      );
    };
  },
});

const AgentPicker = defineComponent<{
  agents: MerchantAgent[];
  query: string;
  onSelect: (agent: MerchantAgent) => void;
}>({
  name: "AgentPicker",
  props: ["agents", "query", "onSelect"],
  setup(props) {
    const filtered = computed(() =>
      props.agents.filter((agent) =>
        agent.name.toLowerCase().startsWith(props.query.toLowerCase()),
      ),
    );

    return () => {
      if (filtered.value.length === 0) return null;

      return (
        <div class="absolute bottom-full left-0 z-20 mb-2 w-64 overflow-hidden rounded-lg border border-base-content/15 bg-base-100 shadow-xl">
          {filtered.value.map((agent) => {
            const colors = AGENT_COLOR_MAP[agent.avatarColor];
            return (
              <button
                key={agent.id}
                onClick={() => props.onSelect(agent)}
                class="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-base-content/5"
              >
                <AgentAvatar agent={agent} size="sm" />
                <div class="min-w-0">
                  <p class={`truncate text-sm font-medium ${colors.text}`}>
                    {agent.name}
                  </p>
                  <p class="truncate text-xs capitalize text-base-content/40">
                    {agent.templateType}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      );
    };
  },
});

const InputBar = defineComponent<{
  onSend: (text: string, agentId: string | null) => void;
  disabled: boolean;
  projectAgents: MerchantAgent[];
  autofocus?: boolean;
  placeholder?: string;
}>({
  name: "InputBar",
  props: ["onSend", "disabled", "projectAgents", "autofocus", "placeholder"],
  setup(props) {
    const text = ref("");
    const textarea = ref<HTMLTextAreaElement | null>(null);
    const atQuery = ref<string | null>(null);
    const selectedAgent = ref<MerchantAgent | null>(null);

    const submit = () => {
      const trimmed = text.value.trim();
      if (!trimmed || props.disabled) return;
      props.onSend(trimmed, selectedAgent.value?.id ?? null);
      text.value = "";
      selectedAgent.value = null;
      atQuery.value = null;
      nextTick(() => {
        if (textarea.value) textarea.value.style.height = "auto";
      });
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        submit();
      }
      if (event.key === "Escape") atQuery.value = null;
    };

    const handleInput = (event: Event) => {
      const element = event.target as HTMLTextAreaElement;
      const value = element.value;
      text.value = value;
      element.style.height = "auto";
      element.style.height = `${Math.min(element.scrollHeight, 180)}px`;

      const atIndex = value.lastIndexOf("@");
      if (atIndex !== -1 && (atIndex === 0 || value[atIndex - 1] === " ")) {
        const query = value.slice(atIndex + 1);
        if (!query.includes(" ")) {
          atQuery.value = query;
          return;
        }
      }
      atQuery.value = null;
    };

    const selectAgent = (agent: MerchantAgent) => {
      const atIndex = text.value.lastIndexOf("@");
      text.value = `${text.value.slice(0, atIndex)}@${agent.name} `;
      selectedAgent.value = agent;
      atQuery.value = null;
      textarea.value?.focus();
    };

    return () => (
      <div class="relative">
        {atQuery.value !== null && (
          <AgentPicker
            agents={props.projectAgents}
            query={atQuery.value}
            onSelect={selectAgent}
          />
        )}
        <div class="rounded-lg border border-base-content/15 bg-base-100 shadow-sm focus-within:border-base-content/30">
          {selectedAgent.value && (
            <div class="flex items-center gap-2 border-b border-base-content/10 px-3 py-2">
              <span class="text-xs text-base-content/40">Routed to</span>
              <AgentAvatar agent={selectedAgent.value} size="xs" />
              <span class="text-xs font-medium text-base-content">
                {selectedAgent.value.name}
              </span>
              <button
                onClick={() => (selectedAgent.value = null)}
                class="ml-auto text-base-content/35 hover:text-base-content/70"
              >
                <span class="material-symbols-outlined" style="font-size:14px">
                  close
                </span>
              </button>
            </div>
          )}
          <div class="flex items-end gap-2 px-3 py-2">
            <button
              class="mb-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-base-content/35 hover:bg-base-content/5 hover:text-base-content/70"
              title="Attach file"
            >
              <span class="material-symbols-outlined" style="font-size:17px">
                attach_file
              </span>
            </button>
            <textarea
              ref={textarea}
              value={text.value}
              onInput={handleInput}
              onKeydown={handleKeydown}
              placeholder={props.placeholder ?? "Ask the project team..."}
              rows={1}
              autofocus={props.autofocus}
              class="max-h-[180px] min-h-[32px] flex-1 resize-none overflow-y-auto bg-transparent py-1.5 text-sm leading-6 text-base-content outline-none placeholder-base-content/30"
            />
            <button
              class="mb-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-base-content/35 hover:bg-base-content/5 hover:text-base-content/70"
              title="Voice note"
            >
              <span class="material-symbols-outlined" style="font-size:17px">
                mic
              </span>
            </button>
            <button
              onClick={submit}
              disabled={!text.value.trim() || props.disabled}
              class="mb-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-primary text-primary-content transition-colors disabled:bg-base-content/10 disabled:text-base-content/30"
              title="Send"
            >
              <span class="material-symbols-outlined" style="font-size:16px">
                arrow_upward
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  },
});

const ModelSelect = defineComponent<{
  models: ModelDefinition[];
  modelId: string | null;
  onChange: (modelId: string) => void;
}>({
  name: "ModelSelect",
  props: ["models", "modelId", "onChange"],
  setup(props) {
    const current = computed(
      () => props.models.find((m) => m.id === props.modelId) ?? props.models[0],
    );
    return () => (
      <div class="flex items-center gap-1">
        <select
          value={props.modelId ?? current.value?.id}
          onChange={(event) =>
            props.onChange((event.target as HTMLSelectElement).value)
          }
          class="select select-xs select-ghost max-w-[160px] text-xs text-base-content/55"
          title={current.value?.description}
        >
          {props.models.map((model) => (
            <option key={model.id} value={model.id}>
              {model.name}
            </option>
          ))}
        </select>
      </div>
    );
  },
});

const ProjectSelect = defineComponent<{
  projectId: string;
  onChange: (projectId: string) => void;
}>({
  name: "ProjectSelect",
  props: ["projectId", "onChange"],
  setup(props) {
    return () => (
      <select
        value={props.projectId}
        onChange={(event) =>
          props.onChange((event.target as HTMLSelectElement).value)
        }
        class="select select-sm w-full border-base-content/10 bg-base-200 text-sm"
      >
        {STUB_PROJECTS.map((project) => (
          <option key={project.id} value={project.id}>
            {project.icon} {project.name}
          </option>
        ))}
      </select>
    );
  },
});

export const ThreadView = defineComponent({
  name: "ThreadView",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const projectId = computed(() => route.params.projectId as string);
    const threadId = computed(() => route.params.threadId as string);
    const isNew = computed(() => threadId.value === "new");
    const store = useIntelligenceStore();
    const project = computed(
      () =>
        store.projects.value.find((item) => item.id === projectId.value) ??
        null,
    );
    const thread = computed(
      () =>
        project.value?.threads.find((item) => item.id === threadId.value) ??
        null,
    );
    const projectColors = computed(
      () =>
        PROJECT_COLOR_MAP[project.value?.color ?? "neutral"] ??
        PROJECT_COLOR_MAP.neutral,
    );
    const projectAgents = computed(
      () =>
        (project.value?.agentIds
          .map((id) => getAgent(id))
          .filter(Boolean) as MerchantAgent[]) ?? [],
    );
    const mentionOptions = computed<ComposerMention[]>(() => {
      const agents =
        projectAgents.value.length > 0 ? projectAgents.value : STUB_AGENTS;
      return agents.map((agent) => ({
        id: agent.id,
        name: agent.name,
        icon: agent.avatarIcon,
        colorClass: AGENT_COLOR_MAP[agent.avatarColor].bg,
      }));
    });
    const trpc = useTrpc();
    const messages = ref<ThreadMessage[]>([]);
    const isStreaming = ref(false);
    const isThreadLoading = ref(false);
    const showBindings = ref(false);
    const messagesEnd = ref<HTMLDivElement | null>(null);
    const useLiveApi = ref(false);
    const models = ref<ModelDefinition[]>([]);
    const composerAgents = ref<ComposerAgent[]>([]);
    const newThreadAgentId = ref<string | null>(null);
    const threadModelId = ref<string | null>(null);
    const threadTitle = ref<string | null>(null);
    const isRenaming = ref(false);
    const renameValue = ref("");
    const totalTokens = ref(0);
    // Old messages carry no modelId; assume the thread's model (or the
    // registry default the model picker shows).
    const costFallbackModelId = computed(
      () => threadModelId.value ?? models.value[0]?.id ?? null,
    );
    const costOf = (message: ThreadMessage) =>
      message.usage
        ? messageCost(message.usage, models.value, costFallbackModelId.value)
        : null;
    // Null unless every agent message with usage has a priced model, so the
    // header never shows a partial total.
    const totalCost = computed(() => {
      let sum = 0;
      for (const message of messages.value) {
        if (!message.usage) continue;
        const cost = costOf(message);
        if (cost == null) return null;
        sum += cost;
      }
      return sum;
    });
    const artifacts = ref<{ id: string; name: string; previewUrl: string }[]>(
      [],
    );
    let loadRequestId = 0;

    const loadModels = async () => {
      try {
        models.value =
          (await trpc.intelligence.listModels.query()) as ModelDefinition[];
      } catch {
        models.value = [];
      }
    };

    const loadAgents = async () => {
      try {
        const agents = (await trpc.intelligence.listAgents.query()) as Array<{
          id: string;
          name: string;
          avatar_icon: string | null;
          avatar_color: string | null;
          description?: string;
        }>;
        composerAgents.value = agents.map((agent) => ({
          id: agent.id,
          name: agent.name,
          icon: agent.avatar_icon ?? undefined,
          colorClass: agent.avatar_color
            ? AGENT_COLOR_MAP[
                agent.avatar_color as keyof typeof AGENT_COLOR_MAP
              ]?.bg
            : undefined,
          description: agent.description,
        }));
        newThreadAgentId.value = composerAgents.value[0]?.id ?? null;
      } catch {
        composerAgents.value = [];
      }
    };

    const loadArtifacts = async (requestId = loadRequestId) => {
      if (isNew.value || !useLiveApi.value) return;
      try {
        const raw = (await trpc.intelligence.listArtifacts.query({
          projectId: projectId.value,
          threadId: threadId.value,
        })) as { id: string; name: string; r2_key: string }[];
        if (requestId !== loadRequestId) return;
        artifacts.value = raw.map((a) => ({
          id: a.id,
          name: a.name,
          previewUrl: `/api/intelligence/reports/${a.r2_key}`,
        }));
      } catch {
        if (requestId !== loadRequestId) return;
        artifacts.value = [];
      }
    };

    const loadMessages = async () => {
      const requestId = ++loadRequestId;
      messages.value = [];
      artifacts.value = [];
      threadTitle.value = null;
      totalTokens.value = 0;

      if (isNew.value) {
        isThreadLoading.value = false;
        return;
      }
      // Avoid clobbering optimistic/streaming local state with a stale
      // wholesale reload while a message is actively streaming in.
      if (isStreaming.value) return;
      isThreadLoading.value = true;
      try {
        const data = (await trpc.intelligence.listMessages.query({
          projectId: projectId.value,
          threadId: threadId.value,
        })) as {
          thread: {
            title: string | null;
            model_id: string | null;
            total_tokens: number;
          } | null;
          messages: Parameters<typeof toThreadMessage>[0][];
        };
        if (requestId !== loadRequestId) return;
        messages.value = data.messages.map(toThreadMessage);
        threadModelId.value = data.thread?.model_id ?? null;
        threadTitle.value = data.thread?.title ?? null;
        if (data.thread?.title) {
          store.updateThreadTitle(
            projectId.value,
            threadId.value,
            data.thread.title,
          );
        }
        totalTokens.value = data.thread?.total_tokens ?? 0;
        useLiveApi.value = true;
        await loadArtifacts(requestId);

        if (requestId === loadRequestId) isThreadLoading.value = false;

        if (store.pendingFirstMessage.value) {
          const pending = store.pendingFirstMessage.value;
          store.pendingFirstMessage.value = null;
          handleSend(pending, null);
        }
      } catch {
        if (requestId !== loadRequestId) return;
        // apps/intelligence not reachable yet (or this is a stub-only thread) — keep stub data.
        messages.value = [...(STUB_MESSAGES[threadId.value] ?? [])];
        useLiveApi.value = false;
        if (requestId === loadRequestId) isThreadLoading.value = false;
      }
    };

    onMounted(() => {
      loadModels();
      loadAgents();
      loadMessages();
    });
    watch(threadId, () => {
      loadMessages();
    });

    const scrollToBottom = () =>
      nextTick(() => messagesEnd.value?.scrollIntoView({ behavior: "smooth" }));
    onMounted(scrollToBottom);
    watch(messages, scrollToBottom, { deep: true });

    const changeModel = async (modelId: string) => {
      threadModelId.value = modelId;
      if (!useLiveApi.value || isNew.value) return;
      try {
        await trpc.intelligence.setThreadModel.mutate({
          projectId: projectId.value,
          threadId: threadId.value,
          modelId,
        });
      } catch {
        // best-effort
      }
    };

    const handleSend = async (
      text: string,
      agentIdOrModelId: string | null,
    ) => {
      if (isStreaming.value) return;

      messages.value.push({
        id: `u${Date.now()}`,
        senderType: "user",
        senderId: "user-1",
        senderName: "You",
        senderInitials: "AB",
        outputs: [
          { type: "markdown", renderer: "markdown", data: { content: text } },
        ],
        createdAt: new Date().toISOString(),
      });
      store.touchThread(projectId.value, threadId.value);

      isStreaming.value = true;

      if (useLiveApi.value) {
        const streamMsg: ThreadMessage = {
          id: `a${Date.now()}`,
          senderType: "agent",
          senderId: "agent",
          senderName: "Agent",
          outputs: [
            { type: "markdown", renderer: "markdown", data: { content: "" } },
          ],
          createdAt: new Date().toISOString(),
          streaming: true,
        };
        messages.value.push(streamMsg);
        const streamMsgId = streamMsg.id;
        const findMsgIndex = () =>
          messages.value.findIndex((m) => m.id === streamMsgId);

        try {
          const res = await fetch(
            `/api/intelligence/projects/${projectId.value}/threads/${threadId.value}/messages/stream`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ text }),
            },
          );
          if (!res.ok) {
            const body = await res.text();
            throw new Error(body || `Intelligence API returned ${res.status}`);
          }
          if (!res.body) throw new Error("no response body");

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const events = buffer.split("\n\n");
            buffer = events.pop() ?? "";
            for (const event of events) {
              const line = event
                .split("\n")
                .find((l) => l.startsWith("data: "));
              if (!line) continue;
              const payload = JSON.parse(line.slice("data: ".length)) as
                | { type: "delta"; text: string }
                | { type: "tool_call"; toolName: string; input?: unknown }
                | {
                    type: "tool_result";
                    toolName: string;
                    output?: unknown;
                    error?: string;
                  }
                | { type: "error"; message: string }
                | { type: "title"; title: string }
                | {
                    type: "done";
                    message: Parameters<typeof toThreadMessage>[0];
                  };

              if (payload.type === "title") {
                threadTitle.value = payload.title;
                store.updateThreadTitle(
                  projectId.value,
                  threadId.value,
                  payload.title,
                );
                continue;
              }

              const msgIndex = findMsgIndex();
              if (msgIndex === -1) continue;

              if (payload.type === "delta") {
                const current = messages.value[msgIndex];
                (current.outputs[0] as any).data.content += payload.text;
              } else if (payload.type === "error") {
                const current = messages.value[msgIndex];
                current.streaming = false;
                (current.outputs[0] as any).data.content =
                  `Sorry, I ran into an issue: ${payload.message}`;
              } else if (payload.type === "tool_call") {
                const current = messages.value[msgIndex];
                current.toolCalls = [
                  ...(current.toolCalls ?? []),
                  {
                    toolName: payload.toolName,
                    input: payload.input,
                    status: "running",
                  },
                ];
              } else if (payload.type === "tool_result") {
                const current = messages.value[msgIndex];
                const calls = current.toolCalls ?? [];
                for (let i = calls.length - 1; i >= 0; i--) {
                  if (
                    calls[i].toolName === payload.toolName &&
                    calls[i].status === "running"
                  ) {
                    calls[i] = {
                      ...calls[i],
                      output: payload.output,
                      error: payload.error,
                      status: payload.error ? "error" : "done",
                    };
                    break;
                  }
                }
                current.toolCalls = [...calls];
              } else if (payload.type === "done") {
                const toolCalls = messages.value[msgIndex].toolCalls;
                const mapped = toThreadMessage(payload.message);
                messages.value[msgIndex] = {
                  ...mapped,
                  toolCalls,
                  id: streamMsgId,
                };
                if (mapped.usage) totalTokens.value += mapped.usage.totalTokens;
                store.touchThread(
                  projectId.value,
                  threadId.value,
                  mapped.createdAt,
                );
              }
            }
          }
        } catch (err) {
          const idx = findMsgIndex();
          const current = idx === -1 ? streamMsg : messages.value[idx];
          current.streaming = false;
          const message = err instanceof Error ? err.message : "Unknown error";
          (current.outputs[0] as any).data.content =
            `Sorry, I ran into an issue talking to the Intelligence API: ${message}`;
        } finally {
          isStreaming.value = false;
          await loadArtifacts();
        }
        return;
      }

      const responder = projectAgents.value[0] ?? null;
      const responseText =
        "I'm running in stub mode. Connect the Intelligence API to get live responses from your restaurant data.";
      const streamMsg: ThreadMessage = {
        id: `a${Date.now()}`,
        senderType: "agent",
        senderId: responder?.id ?? "agent-alex",
        senderName: responder?.name ?? "Agent",
        senderAvatarIcon: responder?.avatarIcon,
        senderAvatarColor: responder?.avatarColor,
        outputs: [
          { type: "markdown", renderer: "markdown", data: { content: "" } },
        ],
        createdAt: new Date().toISOString(),
        streaming: true,
      };

      messages.value.push(streamMsg);
      let index = 0;
      const interval = setInterval(() => {
        (streamMsg.outputs[0] as any).data.content = responseText.slice(
          0,
          index,
        );
        index += 4;
        if (index > responseText.length) {
          (streamMsg.outputs[0] as any).data.content = responseText;
          streamMsg.streaming = false;
          isStreaming.value = false;
          clearInterval(interval);
        }
      }, 25);
    };

    /** From the "new thread" composer: create the thread, navigate to it, then send the first message. */
    const startThread = async (
      text: string,
      modelId: string | null,
      agentId: string | null,
    ) => {
      if (isStreaming.value) return;
      const created = await store.createThread(
        projectId.value,
        undefined,
        modelId,
        agentId,
      );
      await router.replace(
        `/app/intelligence/projects/${projectId.value}/threads/${created.id}`,
      );
      await nextTick();
      useLiveApi.value = true;
      threadModelId.value = modelId;
      await handleSend(text, null);
    };

    const changeProject = (nextProjectId: string) => {
      router.push(`/app/intelligence/projects/${nextProjectId}/threads/new`);
    };

    const startRename = () => {
      renameValue.value = threadTitle.value ?? thread.value?.title ?? "";
      isRenaming.value = true;
    };

    const confirmRename = async () => {
      const title = renameValue.value.trim();
      isRenaming.value = false;
      if (!title || title === (threadTitle.value ?? thread.value?.title))
        return;
      threadTitle.value = title;
      await store.renameThread(projectId.value, threadId.value, title);
    };

    const handleDeleteThread = async () => {
      const ok = window.confirm(
        "Delete this thread? This removes all its messages and attachments and can't be undone.",
      );
      if (!ok) return;
      await store.deleteThread(projectId.value, threadId.value);
      await router.push(
        `/app/intelligence/projects/${projectId.value}/threads/new`,
      );
    };

    return () => {
      if (isNew.value) {
        if (!project.value) {
          return (
            <div class="flex flex-1 items-center justify-center bg-base-100 text-base-content/35">
              <p class="text-sm">Project not found.</p>
            </div>
          );
        }

        return (
          <main class="flex flex-1 flex-col overflow-y-auto bg-base-100">
            <div class="mx-auto flex min-h-full w-full max-w-4xl flex-col px-6 py-5 max-md:px-4">
              <div class="mb-5 rounded-lg border border-base-content/10 bg-base-200/55 p-4">
                <div class="mb-3 flex items-center gap-2 text-xs text-base-content/45">
                  <RouterLink
                    to="/app/intelligence"
                    class="text-accent hover:text-accent/80"
                  >
                    Intelligence
                  </RouterLink>
                  <span
                    class="material-symbols-outlined text-base-content/20"
                    style="font-size:15px"
                  >
                    chevron_right
                  </span>
                  <span>New thread</span>
                </div>
                <div class="mb-4 flex items-center gap-3">
                  <div
                    class={`flex h-11 w-11 items-center justify-center rounded-lg border text-2xl ${projectColors.value.soft}`}
                  >
                    {project.value.icon}
                  </div>
                  <div class="min-w-0">
                    <h1 class="text-xl font-semibold tracking-normal text-base-content">
                      Start a new thread
                    </h1>
                    <p class="mt-1 text-sm leading-5 text-base-content/55">
                      Write the first instruction for this project team.
                    </p>
                  </div>
                </div>

                <div class="mb-3 flex items-center gap-2">
                  <span class="text-xs text-base-content/40">Project</span>
                  <div class="w-56">
                    <ProjectSelect
                      projectId={project.value.id}
                      onChange={changeProject}
                    />
                  </div>
                </div>
              </div>

              <AgentMessageComposer
                onSend={startThread}
                disabled={isStreaming.value}
                models={models.value}
                modelId={threadModelId.value}
                onModelChange={(modelId) => (threadModelId.value = modelId)}
                agents={composerAgents.value}
                agentId={newThreadAgentId.value}
                onAgentChange={(agentId) => (newThreadAgentId.value = agentId)}
                mentions={mentionOptions.value}
                placeholder="What should the agents work on?"
                autofocus
              />

              <div class="mt-3 flex flex-wrap items-center gap-2">
                <span class="text-xs text-base-content/40">
                  Available agents
                </span>
                {(projectAgents.value.length > 0
                  ? projectAgents.value
                  : STUB_AGENTS
                ).map((agent) => {
                  const colors = AGENT_COLOR_MAP[agent.avatarColor];
                  return (
                    <span
                      key={agent.id}
                      class={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs ${colors.bg} ${colors.border} ${colors.text}`}
                    >
                      <AgentAvatar agent={agent} size="xs" />
                      {agent.name}
                    </span>
                  );
                })}
              </div>
            </div>
          </main>
        );
      }

      if (!project.value) {
        return (
          <div class="flex flex-1 items-center justify-center bg-base-100 text-base-content/35">
            <p class="text-sm">Thread not found.</p>
          </div>
        );
      }

      const title =
        threadTitle.value ?? thread.value?.title ?? "Untitled thread";
      const { r2Assets, artifactRepo, schedules } = project.value.bindings;
      const hasBindings =
        r2Assets.length > 0 || !!artifactRepo || schedules.length > 0;

      return (
        <main class="flex h-full min-w-0 flex-1 bg-base-100">
          <section class="flex min-w-0 flex-1 flex-col overflow-hidden">
            <header class="flex flex-shrink-0 items-center gap-2 border-b border-base-content/10 bg-base-100/95 px-4 py-2">
              <RouterLink
                to="/app/intelligence"
                class="rounded-md p-1 text-base-content/35 hover:bg-base-content/5 hover:text-base-content/70"
              >
                <span class="material-symbols-outlined" style="font-size:18px">
                  home
                </span>
              </RouterLink>
              <span
                class="material-symbols-outlined text-base-content/20"
                style="font-size:15px"
              >
                chevron_right
              </span>
              <RouterLink
                to={`/app/intelligence/projects/${project.value.id}/settings`}
                class={`flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-xs ${projectColors.value.soft}`}
              >
                <span>{project.value.icon}</span>
                <span class="max-w-[180px] truncate">{project.value.name}</span>
              </RouterLink>
              <span
                class="material-symbols-outlined text-base-content/20"
                style="font-size:15px"
              >
                chevron_right
              </span>
              {isRenaming.value ? (
                <input
                  type="text"
                  value={renameValue.value}
                  onInput={(event) =>
                    (renameValue.value = (
                      event.target as HTMLInputElement
                    ).value)
                  }
                  onKeydown={(event) => {
                    if (event.key === "Enter") confirmRename();
                    if (event.key === "Escape") isRenaming.value = false;
                  }}
                  onBlur={confirmRename}
                  // eslint-disable-next-line vue/no-ref-as-operand
                  ref={(el) => (el as HTMLInputElement | null)?.focus()}
                  class="input input-xs min-w-0 flex-1 border-base-content/10 bg-base-200 text-sm font-medium"
                />
              ) : (
                <span class="min-w-0 flex-1 truncate text-sm font-medium text-base-content/70">
                  {title}
                </span>
              )}
              {!isNew.value && (
                <div class="dropdown dropdown-end">
                  <button
                    tabindex={0}
                    class="btn btn-xs btn-ghost btn-square text-base-content/45"
                  >
                    <span
                      class="material-symbols-outlined"
                      style="font-size:16px"
                    >
                      more_vert
                    </span>
                  </button>
                  <ul
                    tabindex={0}
                    class="dropdown-content menu z-10 w-40 rounded-md border border-base-content/10 bg-base-100 p-1 shadow-md"
                  >
                    <li>
                      <a onClick={startRename}>
                        <span
                          class="material-symbols-outlined"
                          style="font-size:16px"
                        >
                          edit
                        </span>
                        Rename
                      </a>
                    </li>
                    <li>
                      <a onClick={handleDeleteThread} class="text-error">
                        <span
                          class="material-symbols-outlined"
                          style="font-size:16px"
                        >
                          delete
                        </span>
                        Delete
                      </a>
                    </li>
                  </ul>
                </div>
              )}
              {totalTokens.value > 0 && (
                <span
                  class="text-xs text-base-content/35"
                  title="Total tokens consumed by this thread, with cost at model list prices"
                >
                  {formatTokens(totalTokens.value)} tok
                  {totalCost.value != null &&
                    ` · ${formatCost(totalCost.value)}`}
                </span>
              )}
              <button
                onClick={() => (showBindings.value = true)}
                class="btn btn-xs btn-ghost gap-1 text-accent"
              >
                <span class="material-symbols-outlined" style="font-size:14px">
                  tune
                </span>
                Bindings
              </button>
            </header>

            <div class="flex-1 overflow-y-auto">
              <div class="mx-auto w-full max-w-4xl px-6 py-5 max-md:px-4">
                {isThreadLoading.value ? (
                  <div class="rounded-lg border border-base-content/10 bg-base-200/35 px-4 py-8 text-center">
                    <span class="loading loading-spinner loading-sm text-accent" />
                    <p class="mt-3 text-sm font-medium text-base-content/55">
                      Loading thread...
                    </p>
                  </div>
                ) : messages.value.length === 0 ? (
                  <div class="rounded-lg border border-dashed border-base-content/15 bg-base-200/35 px-4 py-8 text-center">
                    <p class="text-sm font-medium text-base-content/60">
                      Start the conversation
                    </p>
                    <div class="mt-4 flex flex-wrap justify-center gap-2">
                      {[
                        "What should we focus on today?",
                        "Show me a quick summary",
                        "What's new this week?",
                      ].map((prompt) => (
                        <button
                          key={prompt}
                          onClick={() => handleSend(prompt, null)}
                          class="rounded-md border border-base-content/15 px-2.5 py-1.5 text-xs text-base-content/55 hover:bg-base-content/5"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    {messages.value.map((message) => (
                      <MessageRow
                        key={message.id}
                        message={message}
                        cost={costOf(message)}
                      />
                    ))}
                  </div>
                )}
                <div ref={messagesEnd} />
              </div>
            </div>

            <div class="flex-shrink-0 border-t border-base-content/10 bg-base-100 px-4 py-3">
              <div class="mx-auto w-full max-w-4xl">
                <AgentMessageComposer
                  onSend={handleSend}
                  disabled={isStreaming.value}
                  models={models.value}
                  modelId={threadModelId.value}
                  onModelChange={changeModel}
                  mentions={mentionOptions.value}
                  placeholder="Ask, mention an agent, or request a change..."
                  compact
                />
              </div>
            </div>
          </section>

          <aside class="hidden xl:flex flex-col flex-shrink-0 border-l border-base-content/10 bg-base-200/45 transition-all duration-300 ease-in-out w-6 hover:w-72 overflow-hidden group/sidebar relative h-full">
            {/* Collapsed state indicator */}
            <div class="absolute inset-y-0 left-0 w-6 flex flex-col items-center justify-center text-base-content/25 group-hover/sidebar:opacity-0 transition-opacity duration-150 pointer-events-none">
              <span class="material-symbols-outlined" style="font-size:16px">
                info
              </span>
            </div>

            {/* Expanded content */}
            <div class="w-64 flex-shrink-0 p-4 opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-300 pointer-events-none group-hover/sidebar:pointer-events-auto overflow-y-auto h-full flex flex-col gap-5">
              <div>
                <p class="mb-2 text-xs font-medium uppercase tracking-wide text-accent/70">
                  Project context
                </p>
                <p class="text-sm leading-6 text-base-content/60">
                  {project.value.description}
                </p>
              </div>
              <div>
                <p class="mb-2 text-xs font-medium uppercase tracking-wide text-accent/70">
                  Agents
                </p>
                <div class="flex flex-col gap-1.5">
                  {projectAgents.value.map((agent) => {
                    const colors = AGENT_COLOR_MAP[agent.avatarColor];
                    return (
                      <div
                        key={agent.id}
                        class="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-base-content/5"
                      >
                        <AgentAvatar agent={agent} size="sm" />
                        <div class="min-w-0">
                          <p
                            class={`truncate text-sm font-medium ${colors.text}`}
                          >
                            {agent.name}
                          </p>
                          <p class="truncate text-xs text-base-content/40">
                            {agent.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {artifacts.value.length > 0 && (
                <div>
                  <p class="mb-2 text-xs font-medium uppercase tracking-wide text-accent/70">
                    Reports
                  </p>
                  <div class="flex flex-col gap-1">
                    {artifacts.value.map((artifact) => (
                      <a
                        key={artifact.id}
                        href={artifact.previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-base-content/65 hover:bg-base-content/5"
                      >
                        <span
                          class="material-symbols-outlined text-base-content/35"
                          style="font-size:16px"
                        >
                          description
                        </span>
                        <span class="min-w-0 truncate">{artifact.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {hasBindings && (
                <div>
                  <p class="mb-2 text-xs font-medium uppercase tracking-wide text-accent/70">
                    Bindings
                  </p>
                  <div class="flex flex-col gap-1 text-sm text-base-content/55">
                    {r2Assets.length > 0 && (
                      <div class="flex items-center gap-2 rounded-md px-2 py-1.5">
                        <span
                          class="material-symbols-outlined text-base-content/35"
                          style="font-size:15px"
                        >
                          folder
                        </span>
                        {r2Assets.length} files
                      </div>
                    )}
                    {artifactRepo && (
                      <div class="flex items-center gap-2 rounded-md px-2 py-1.5">
                        <span
                          class="material-symbols-outlined text-base-content/35"
                          style="font-size:15px"
                        >
                          language
                        </span>
                        Website repo
                      </div>
                    )}
                    {schedules.length > 0 && (
                      <div class="flex items-center gap-2 rounded-md px-2 py-1.5">
                        <span
                          class="material-symbols-outlined text-base-content/35"
                          style="font-size:15px"
                        >
                          schedule
                        </span>
                        {schedules.length} schedules
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </aside>
          {showBindings.value && (
            <RightSheet
              isOpen={showBindings.value}
              title={`${project.value.name} bindings`}
              onClose={() => (showBindings.value = false)}
              width="720px"
            >
              <div class="px-5 py-5">
                <div class="mb-6">
                  <div
                    class={`mb-3 flex h-12 w-12 items-center justify-center rounded-lg border text-2xl ${projectColors.value.soft}`}
                  >
                    {project.value.icon}
                  </div>
                  <p class="text-sm text-base-content/55">
                    {project.value.description}
                  </p>
                </div>
                <ProjectBindingsPanel project={project.value} />
              </div>
            </RightSheet>
          )}
        </main>
      );
    };
  },
});
